import { inject, injectable } from 'inversify';
import { Platform } from 'src/environment';
import { TYPES } from '../di/types';
import { asyncLoadLibrary } from '../utilities/asyncLoadLibrary';
import type { CredentialService } from './credential-service';

export interface InAuthService {
  sendFraudPageView(trackId?): void;
}

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class InAuthServiceImpl implements InAuthService {
  private inAuthLoaded = Promise.resolve();

  constructor(
    @inject(TYPES.Window)
    private readonly windowInstance: Window,
    @inject(TYPES.AppConfig)
    private readonly appConfig: AppConfig,
    @inject(TYPES.CredentialsService)
    private readonly credentialService: CredentialService,
  ) {
    this.init();
  }

  private init(): void {
    if (!this.windowInstance._cc) {
      this.inAuthLoaded = this.loadInAuth();
    }
  }

  public async sendFraudPageView(trackingId = ''): Promise<void> {
    await this.inAuthLoaded;

    const {
      inAuthCollectorFlags,
      inAuthBehavioralFraudDetection,
      inAuthSiteKey,
      inAuthCollectorUrl,
    } = this.appConfig.inAuthConfig;
    const inAuthQueue = this.windowInstance._cc;
    const protocol = this.windowInstance.document.location.protocol;

    if (inAuthQueue) {
      inAuthQueue.push([
        'ci',
        {
          sid: inAuthSiteKey,
          tid: `${this.credentialService.getCredentialUdid()},${trackingId}`,
        },
      ]);
      inAuthQueue.push(['cf', inAuthCollectorFlags]);
      inAuthQueue.push(['bfdc', inAuthBehavioralFraudDetection]);
      inAuthQueue.push(['run', `${protocol}//${inAuthCollectorUrl}`]);
    }
  }

  private async loadInAuth(): Promise<void> {
    const windowInstance = this.windowInstance;
    const protocol = windowInstance.document.location.protocol;
    const inAuthUrl = this.appConfig.inAuthConfig.inAuthCollectorUrl;
    const siteKey = this.appConfig.inAuthConfig.inAuthSiteKey;
    const time = new Date().getTime();
    const inAuthScriptUrl = `${protocol}//${inAuthUrl}/cc.js?sid=${siteKey}&ts=${time}`;

    if (this.appConfig.platform === Platform.WEB) {
      await asyncLoadLibrary({
        id: 'in_auth',
        url: inAuthScriptUrl,
        attributes: { async: '' },
        isAppend: false,
        onLoad() {
          windowInstance._cc = windowInstance._cc || [];
        },
      });
    }
  }
}
